/**
 * Live render of a Page's layout — Container/Row/Column become CSS-only
 * divs (no toolbar, no border, no padding, gap-1 between flex/grid children).
 * Slot renders ONE item per its `preset` via <ItemSlot>, Carousel renders
 * ALL items in the referenced group as slides with dot-nav per `preset`.
 *
 * Used by /homepage. Layout authoring (adding/removing/reorder of
 * Container/Row/Column/Slot) is NOT possible here — that's the
 * super_admin's job at /pages/$id/edit. This surface is read-mostly: it
 * shows the layout structure roughly as the public site would render it,
 * with the actual hydrated items inline.
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import { AlertTriangle, Link as LinkIcon, Megaphone } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import type {
  CarouselNode,
  ColumnNode,
  ContainerNode,
  HydratedGroupItem,
  LabelNode,
  LayoutNode,
  PageLayout,
  RowNode,
  SlotNode,
} from '@news-cms/shared';
import {
  groupContentPreviewQueryOptions,
  groupContentsListQueryOptions,
} from '@/lib/group-contents';
import { slotOverridesListQueryOptions } from '@/lib/slot-overrides';
import { ItemSlot } from './layout-preview';
import { SlotGroupEditor } from './slot-group-editor';

interface PageLiveRenderProps {
  layout: PageLayout;
  /** Page slug to look up active slot overrides. Defaults to 'home'
   *  (the system page used by /homepage). Pass the actual slug when
   *  rendering live preview for a non-home page. */
  pageSlug?: string;
}

export function PageLiveRender({ layout, pageSlug = 'home' }: PageLiveRenderProps) {
  const groupsQuery = useQuery(groupContentsListQueryOptions({ page: 1, pageSize: 100 }));
  const overridesQuery = useQuery(slotOverridesListQueryOptions({ pageSlug, status: 'active' }));

  // Split active overrides into slot (slideIndex null) and carousel-slide
  // (slideIndex integer) for O(1) ribbon lookup inside the recursive walker.
  const overriddenSlotIds = new Set<string>();
  const overriddenCarouselSlides = new Map<string, Set<number>>();
  for (const item of overridesQuery.data?.items ?? []) {
    if (item.slideIndex === null) {
      overriddenSlotIds.add(item.slotId);
    } else {
      let inner = overriddenCarouselSlides.get(item.slotId);
      if (!inner) {
        inner = new Set<number>();
        overriddenCarouselSlides.set(item.slotId, inner);
      }
      inner.add(item.slideIndex);
    }
  }

  const root = layout.root;
  const isLoading = groupsQuery.isLoading;

  if (root.children.length === 0) {
    return (
      <EmptyNotice>
        หน้านี้ยังไม่มี slot — ให้ super_admin เพิ่มที่ <code>/pages/page-home/edit</code>
      </EmptyNotice>
    );
  }

  return (
    <NodeRender
      node={root}
      pageSlug={pageSlug}
      groupsBySlug={indexBySlug(groupsQuery.data?.items ?? [])}
      isLoading={isLoading}
      overriddenSlotIds={overriddenSlotIds}
      overriddenCarouselSlides={overriddenCarouselSlides}
    />
  );
}

interface NodeRenderProps {
  node: LayoutNode;
  /** Threaded down so SlotGroupEditor's PR tab can deep-link to /promos
   *  with the current page pre-selected. */
  pageSlug: string;
  groupsBySlug: Map<string, { id: string }>;
  isLoading: boolean;
  overriddenSlotIds: Set<string>;
  overriddenCarouselSlides: Map<string, Set<number>>;
}

function NodeRender({ node, ...rest }: NodeRenderProps) {
  if (node.type === 'container') {
    return <ContainerRender node={node} {...rest} />;
  }
  if (node.type === 'row') {
    return <RowRender node={node} {...rest} />;
  }
  if (node.type === 'column') {
    return <ColumnRender node={node} {...rest} />;
  }
  if (node.type === 'carousel') {
    return <CarouselRender node={node} {...rest} />;
  }
  if (node.type === 'label') {
    return <LabelRender node={node} />;
  }
  return <SlotRender node={node} {...rest} />;
}

const LABEL_CLASS: Record<LabelNode['preset'], string> = {
  h1: 'text-3xl font-bold',
  h2: 'text-2xl font-semibold',
  h3: 'text-xl font-semibold',
  h4: 'text-lg font-medium',
  h5: 'text-base font-medium',
  h6: 'text-sm font-medium',
};

function LabelRender({ node }: { node: LabelNode }) {
  const className = LABEL_CLASS[node.preset];
  if (node.url) {
    return (
      <a
        href={node.url}
        target="_blank"
        rel="noopener noreferrer"
        className={`${className} text-sky-700 hover:underline dark:text-sky-300`}
      >
        {node.text}
      </a>
    );
  }
  return <span className={`${className} text-slate-800 dark:text-slate-100`}>{node.text}</span>;
}

function ContainerRender({
  node,
  ...rest
}: { node: ContainerNode } & Omit<NodeRenderProps, 'node'>) {
  return (
    <div className="flex flex-col gap-1 p-0">
      {node.children.map((c) => (
        <NodeRender key={c.id} node={c} {...rest} />
      ))}
    </div>
  );
}

function RowRender({ node, ...rest }: { node: RowNode } & Omit<NodeRenderProps, 'node'>) {
  // Bootstrap-style 12-col grid; gap-1 (= 4px) between columns.
  return (
    <div className="grid grid-cols-12 gap-1 p-0">
      {node.children.map((c) => (
        <NodeRender key={c.id} node={c} {...rest} />
      ))}
    </div>
  );
}

function ColumnRender({ node, ...rest }: { node: ColumnNode } & Omit<NodeRenderProps, 'node'>) {
  // Dynamic span → inline style (Tailwind JIT doesn't handle every dynamic
  // class without a safelist; inline is simplest + correct).
  return (
    <div
      style={{ gridColumn: `span ${node.span} / span ${node.span}` }}
      className="flex min-w-0 flex-col gap-1 p-0"
    >
      {node.children.map((c) => (
        <NodeRender key={c.id} node={c} {...rest} />
      ))}
    </div>
  );
}

function SlotRender({
  node,
  groupsBySlug,
  isLoading,
  overriddenSlotIds,
  pageSlug,
}: { node: SlotNode } & Omit<NodeRenderProps, 'node'>) {
  if (!node.groupSlug) {
    return (
      <NoticeRow tone="amber">
        Slot ว่าง — assign group ที่ <code>/pages/page-home/edit</code>
      </NoticeRow>
    );
  }
  if (isLoading) {
    return (
      <div className="rounded-md border border-dashed border-slate-200 p-3 text-center text-xs text-slate-400 dark:border-slate-700">
        กำลังโหลด slot…
      </div>
    );
  }
  const group = groupsBySlug.get(node.groupSlug);
  if (!group) {
    return (
      <NoticeRow tone="red">
        Group <code>{node.groupSlug}</code> หายไป — สร้างใหม่หรือ assign slot ใหม่ที่{' '}
        <code>/pages/page-home/edit</code>
      </NoticeRow>
    );
  }
  return (
    <SlotItemPreview
      groupId={group.id}
      slug={node.groupSlug}
      node={node}
      isOverride={overriddenSlotIds.has(node.id)}
      pageSlug={pageSlug}
    />
  );
}

function SlotItemPreview({
  groupId,
  slug,
  node,
  isOverride,
  pageSlug,
}: {
  groupId: string;
  slug: string;
  node: SlotNode;
  isOverride: boolean;
  pageSlug: string;
}) {
  const previewQuery = useQuery(groupContentPreviewQueryOptions(groupId));
  const items: HydratedGroupItem[] = previewQuery.data ?? [];
  // Defensive fallbacks — legacy slot JSON may lack preset/itemIndex.
  const effectiveIndex = typeof node.itemIndex === 'number' ? node.itemIndex : 0;
  const effectivePreset = node.preset ?? 'card';
  const item = items[effectiveIndex];
  const [editOpen, setEditOpen] = useState(false);

  if (previewQuery.isLoading) {
    return (
      <div className="rounded-md border border-dashed border-slate-200 p-3 text-center text-[11px] text-slate-400 dark:border-slate-700">
        กำลังโหลด preview…
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <NoticeRow tone="amber">
        Group <code>{slug}</code> ยังไม่มี item
      </NoticeRow>
    );
  }
  if (effectiveIndex >= items.length) {
    return (
      <NoticeRow tone="amber">
        Group <code>{slug}</code> มี {items.length} items — ตำแหน่ง #{effectiveIndex} ไม่มีข้อมูล
      </NoticeRow>
    );
  }
  return (
    <>
      <div className="relative">
        {isOverride && <PromoRibbon />}
        <ClickToEdit onOpen={() => setEditOpen(true)} title="คลิกเพื่อแก้ไข Group Content">
          <ItemSlot item={item} variant={effectivePreset} />
        </ClickToEdit>
      </div>
      <SlotGroupEditor
        groupId={groupId}
        layoutTemplateId={null}
        embedded={{ open: editOpen, onOpenChange: setEditOpen }}
        prContext={{ pageSlug, slotId: node.id, slideIndex: null }}
      />
    </>
  );
}

/** Amber 📣 ribbon overlaid on a slot when an active slot_override is
 *  substituting its item. Sits at top-left with a subtle pulse so admin
 *  immediately notices which slots are running a PR right now. */
function PromoRibbon() {
  return (
    <div
      className="pointer-events-none absolute left-1 top-1 z-10 flex items-center gap-1 rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-medium text-white shadow-sm"
      title="กำลังแสดง PR (ข่าวประชาสัมพันธ์)"
    >
      <Megaphone className="h-3 w-3" />
      PR
    </div>
  );
}

function CarouselRender({
  node,
  groupsBySlug,
  isLoading,
  overriddenCarouselSlides,
  pageSlug,
}: { node: CarouselNode } & Omit<NodeRenderProps, 'node'>) {
  if (!node.groupSlug) {
    return <NoticeRow tone="amber">Carousel ว่าง — assign group</NoticeRow>;
  }
  if (isLoading) {
    return (
      <div className="rounded-md border border-dashed border-indigo-200 p-3 text-center text-xs text-indigo-400 dark:border-indigo-900/50">
        กำลังโหลด carousel…
      </div>
    );
  }
  const group = groupsBySlug.get(node.groupSlug);
  if (!group) {
    return (
      <NoticeRow tone="red">
        Group <code>{node.groupSlug}</code> หายไป
      </NoticeRow>
    );
  }
  return (
    <CarouselSlidesPreview
      groupId={group.id}
      slug={node.groupSlug}
      node={node}
      overriddenSlides={overriddenCarouselSlides.get(node.id) ?? null}
      pageSlug={pageSlug}
    />
  );
}

function CarouselSlidesPreview({
  groupId,
  slug,
  node,
  overriddenSlides,
  pageSlug,
}: {
  groupId: string;
  slug: string;
  node: CarouselNode;
  /** Slide indexes (0-based) on THIS carousel with an active PR right now. */
  overriddenSlides: Set<number> | null;
  pageSlug: string;
}) {
  const previewQuery = useQuery(groupContentPreviewQueryOptions(groupId));
  const items: HydratedGroupItem[] = previewQuery.data ?? [];
  const effectivePreset = node.preset ?? 'card';
  const [activeIdx, setActiveIdx] = useState(0);
  const safeIdx = items.length === 0 ? 0 : Math.min(activeIdx, items.length - 1);
  const activeItem = items[safeIdx];
  const [editOpen, setEditOpen] = useState(false);
  const activeSlideIsOverridden = overriddenSlides?.has(safeIdx) ?? false;

  if (previewQuery.isLoading) {
    return (
      <div className="rounded-md border border-dashed border-indigo-200 p-3 text-center text-[11px] text-indigo-400 dark:border-indigo-900/50">
        กำลังโหลด slides…
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <NoticeRow tone="amber">
        Group <code>{slug}</code> ยังไม่มี item
      </NoticeRow>
    );
  }
  return (
    <div className="rounded-md border border-dashed border-indigo-300 bg-indigo-50/40 p-2 dark:border-indigo-800/60 dark:bg-indigo-950/20">
      <div className="relative">
        {activeSlideIsOverridden && <PromoRibbon />}
        <ClickToEdit onOpen={() => setEditOpen(true)} title="คลิกเพื่อแก้ไข Group Content">
          <ItemSlot item={activeItem} variant={effectivePreset} size="hero" />
        </ClickToEdit>
      </div>
      <div className="mt-2 flex items-center justify-center gap-1.5">
        {items.map((it, i) => {
          const slideOverridden = overriddenSlides?.has(i) ?? false;
          return (
            <button
              key={`${it.type}-${i}`}
              type="button"
              onClick={() => setActiveIdx(i)}
              title={slideOverridden ? `Slide ${i + 1} (มี PR)` : `Slide ${i + 1}`}
              className={`h-2 rounded-full transition ${
                i === safeIdx
                  ? slideOverridden
                    ? 'w-4 bg-amber-500'
                    : 'w-4 bg-indigo-500'
                  : slideOverridden
                    ? 'w-2 bg-amber-300 hover:bg-amber-400 dark:bg-amber-700 dark:hover:bg-amber-500'
                    : 'w-2 bg-indigo-200 hover:bg-indigo-400 dark:bg-indigo-800 dark:hover:bg-indigo-600'
              }`}
            />
          );
        })}
      </div>
      <div className="mt-1 text-center text-[10px] text-indigo-500/70">
        {safeIdx + 1} / {items.length}
        {overriddenSlides && overriddenSlides.size > 0 && (
          <span className="ml-1 text-amber-600 dark:text-amber-400">
            ({overriddenSlides.size} slide มี PR)
          </span>
        )}
      </div>
      <SlotGroupEditor
        groupId={groupId}
        layoutTemplateId={null}
        embedded={{ open: editOpen, onOpenChange: setEditOpen }}
        prContext={{ pageSlug, slotId: node.id, slideIndex: safeIdx }}
      />
    </div>
  );
}

function indexBySlug<T extends { slug: string; id: string }>(
  items: T[],
): Map<string, { id: string }> {
  const map = new Map<string, { id: string }>();
  for (const item of items) map.set(item.slug, { id: item.id });
  return map;
}

function EmptyNotice({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900/40">
      <LinkIcon className="mx-auto mb-2 h-4 w-4 text-slate-400" />
      {children}
    </div>
  );
}

/** Wrap a preview item so a click anywhere on the card opens the group's
 *  edit popup. Clicks on internal `<a>` / `<button>` (e.g. the article
 *  link icon, the carousel dots) keep their native behavior — they don't
 *  also open the modal. */
function ClickToEdit({
  onOpen,
  title,
  children,
}: {
  onOpen: () => void;
  title: string;
  children: ReactNode;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      title={title}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('a, button')) return;
        onOpen();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className="cursor-pointer rounded outline-none ring-offset-1 transition hover:ring-2 hover:ring-sky-300 focus-visible:ring-2 focus-visible:ring-sky-400 dark:hover:ring-sky-700"
    >
      {children}
    </div>
  );
}

function NoticeRow({ tone, children }: { tone: 'amber' | 'red'; children: ReactNode }) {
  const cls =
    tone === 'amber'
      ? 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300'
      : 'border-red-300 bg-red-50 text-red-800 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300';
  return (
    <div className={`flex items-center gap-2 rounded-md border px-3 py-2 text-xs ${cls}`}>
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}
